import DownChevron from "@client/icons/DownChevron";
import clsx from "clsx";
import { ReactNode, useState } from "react";
import type { ActionLogItem } from "./getEventLogItems";

import { getUserShortname } from "@engine/round/accessors";
import { RoundAggregate } from "@engine/round/aggregate";

export function ShowEventLogItem({
  round,
  item,
}: {
  round: RoundAggregate;
  item: ActionLogItem;
}) {
  const isEventGroup = Array.isArray(item);
  const firstOrOnlyAction = isEventGroup ? item[0] : item;

  const actingUserShortname = getUserShortname(
    round,
    firstOrOnlyAction.actingAsUserId,
  );
  const underlyingUserShortname = getUserShortname(
    round,
    firstOrOnlyAction.underlyingUserId,
  );
  const userLabel =
    actingUserShortname === underlyingUserShortname ? (
      `User: ${actingUserShortname}`
    ) : (
      <span>
        User: {underlyingUserShortname}
        <span className="text-orange-500 ml-1">
          acting as {actingUserShortname}
        </span>
      </span>
    );

  const summaryWithUser = (
    <>
      {userLabel} - {firstOrOnlyAction.payload.type}
    </>
  );

  const timestamp: string | undefined =
    "timestamp" in firstOrOnlyAction
      ? (firstOrOnlyAction.timestamp as string)
      : undefined;

  const details = (
    <code>
      <pre>{JSON.stringify(firstOrOnlyAction.payload, null, 2)}</pre>
    </code>
  );

  return (
    <EventLogItemContainer
      timestamp={timestamp}
      summary={summaryWithUser}
      details={details}
    />
  );
}

const EventLogItemContainer = ({
  timestamp,
  summary,
  details,
}: {
  timestamp: string | undefined;
  summary: ReactNode;
  details: ReactNode | null;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasExpander = !!details;

  return (
    <div className="flex items-center p-2 border-b border-stone-700">
      {hasExpander && (
        <div
          className={clsx(
            "shrink-0 w-5 h-5 fill-stone-700 dark:fill-white transform cursor-pointer",
            isOpen ? "self-start" : "-rotate-90",
          )}
          onClick={() => setIsOpen(!isOpen)}
        >
          <DownChevron />
        </div>
      )}
      <div className="w-full">
        <div
          className={clsx(
            "flex justify-between text-sm dark:text-stone-300",
            hasExpander && "cursor-pointer",
          )}
          onClick={() => {
            if (hasExpander) {
              setIsOpen(!isOpen);
            }
          }}
        >
          <span>{summary}</span>

          {timestamp && <div>{new Date(timestamp).toLocaleString()}</div>}
        </div>

        {isOpen && <div className="text-xs mt-1">{details}</div>}
      </div>
    </div>
  );
};
